import { Box, Card, Divider, Heading, Text, TextButton } from '@wix/design-system';
import { ChevronDown, ChevronUp } from '@wix/wix-ui-icons-common/lazy';
import { CoreDomainSearchStore, DomainSearchError, DomainSearchSuiteModule } from '@wix/domains-search-suite';
import { isValidDomain, formatInputPrefixAndMissingTld } from '@wix/domains-serverless-interface';
import { DomainsApiClient } from '@wix/domains-serverless-interface/react-kit';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { HeaderWrapper } from '../../../Components/Headers/HeaderWrapper';
import { useBelowFoldImpression } from '../../../hooks/useBelowFoldImpression';
import { useIsInView, useIsBelowViewport } from '../../../hooks/useIsInView';
import { ExploreSimilarButton } from '../../../Components/ExploreSimilarButton/ExploreSimilarButton';
import { useWhoisLookup } from '../../../hooks/useWhoisLookup';
import { useWhoisMobileOptimizations } from '../../../hooks/useWhoisMobileOptimizations';
import { useSettledMethods } from '../../../hooks/useSettledMethod';
import { ANONYMOUS_SETTLEMENTS } from '../../../utils/anonymousSettlements';
import { DomainSearchGateModule } from '../../../module/domainSearchGate.module';
import { useDomainSearchGateBi } from '../../../hooks/useDomainSearchGateBi';
import { ViewMode } from '../../../types';
import { DomainInfoSection, RegistrantSection, RegistrarSection, TosSection, RawDataSection, RawOnlySection, AlternativesPanel, WhoisSkeleton, WhoisPageError, WhoisInlineError } from './components';
import { resolveWhoisRedirect, buildResultsUrl } from './whoisRedirect';
import s from './whoisResultsPage.scss';
function useQueryParam(name) {
  const {
    search
  } = useLocation();
  return new URLSearchParams(search).get(name);
}
function getRegistrantAvailability(registrant) {
  if (registrant.redacted) {
    return 'not available';
  }
  const fields = [registrant.name, registrant.org, registrant.email, registrant.phone, registrant.address];
  const filled = fields.filter(Boolean).length;
  if (filled === 0) {
    return 'not available';
  }
  if (filled === fields.length) {
    return 'available';
  }
  return 'partial';
}
function getWhoisBiParams(domain, d) {
  return {
    domain,
    search_term: domain,
    RDAP_covered: d.source === 'RDAP',
    registrar: d.registrar.name,
    registrar_referral_url: d.registrar.url,
    registrant_information_availability: getRegistrantAvailability(d.registrant)
  };
}
const WhoisResultsPageContent = _ref => {
  let {
    onDomainNotFound
  } = _ref;
  DomainSearchGateModule.SDK.hooks.useDomainsSearchGateBM();
  useDomainSearchGateBi({
    viewMode: ViewMode.WHOIS
  });
  const {
    t,
    Trans
  } = DomainSearchGateModule.internals.useTranslations();
  const rawQuery = useQueryParam('query');
  const domain = rawQuery ? formatInputPrefixAndMissingTld(rawQuery) : null;
  const history = useHistory();
  const {
    data,
    isLoading,
    error,
    retry
  } = useWhoisLookup(domain || '');
  const {
    status: domainActionStatus,
    loading: domainActionLoading
  } = DomainSearchSuiteModule.SDK.useSearchActionResolver();
  const mobileOptimizations = useWhoisMobileOptimizations();
  const [infoExpanded, setInfoExpanded] = useState(false);
  const dataRef = useRef(data);
  dataRef.current = data;
  const whoisCardRef = useRef(null);
  const alternativesPanelRef = useRef(null);
  const voucherBannerRef = useRef(null);
  useEffect(() => {
    const d = dataRef.current;
    if (!d || !domain) {
      return;
    }
    DomainSearchGateModule.internals.bi.onWhoisView == null || DomainSearchGateModule.internals.bi.onWhoisView(getWhoisBiParams(domain, d));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, data == null ? void 0 : data.source]);
  useBelowFoldImpression(whoisCardRef, !!data && !!domain, () => {
    DomainSearchGateModule.internals.bi.onWhoisBelowFoldImpression == null || DomainSearchGateModule.internals.bi.onWhoisBelowFoldImpression({
      ...getWhoisBiParams(domain, dataRef.current),
      window_height: window.innerHeight,
      window_width: window.innerWidth
    });
  });
  const whoisBiParams = data && domain ? getWhoisBiParams(domain, data) : undefined;
  const alternativesInView = useIsInView(alternativesPanelRef, {
    enabled: !!data && !mobileOptimizations
  });
  const alternativesBelowFold = useIsBelowViewport(alternativesPanelRef, {
    enabled: !!data && mobileOptimizations
  });
  const showExploreButton = !!data && !isLoading && (mobileOptimizations ? alternativesBelowFold : !alternativesInView);
  const handleExploreClick = useCallback(() => {
    var _ref2;
    if (whoisBiParams) {
      DomainSearchGateModule.internals.bi.onWhoisGeneralAction == null || DomainSearchGateModule.internals.bi.onWhoisGeneralAction({
        ...whoisBiParams,
        action: 'click',
        button_name: 'explore similar domains'
      });
    }
    (_ref2 = voucherBannerRef.current ?? alternativesPanelRef.current) == null || _ref2.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }, [whoisBiParams]);
  const handleToggleInfo = useCallback(() => {
    setInfoExpanded(prev => {
      const next = !prev;
      if (whoisBiParams) {
        DomainSearchGateModule.internals.bi.onWhoisGeneralAction == null || DomainSearchGateModule.internals.bi.onWhoisGeneralAction({
          ...whoisBiParams,
          action: next ? 'expand' : 'collapse',
          button_name: 'show more'
        });
      }
      return next;
    });
  }, [whoisBiParams]);
  const handleBackClick = useCallback(() => {
    history.goBack();
  }, [history]);
  const handleSearchSubmit = useCallback(filters => {
    history.push(buildResultsUrl(formatInputPrefixAndMissingTld(filters.query)));
  }, [history]);
  useEffect(() => {
    if (!domain) {
      return;
    }
    CoreDomainSearchStore.setQuery(domain);
    CoreDomainSearchStore.setSubmittedFilters({
      query: domain,
      tlds: [],
      keywords: [],
      style: ''
    });
    if (isValidDomain(domain)) {
      CoreDomainSearchStore.setSubmittedDomain(domain);
    }
  }, [domain]);
  useEffect(() => {
    return () => {
      CoreDomainSearchStore.resetStore();
    };
  }, []);
  const redirectUrl = resolveWhoisRedirect({
    domain,
    isLoading,
    actionStatus: domainActionStatus,
    errorStatus: error == null ? void 0 : error.status
  });
  // Errors that would otherwise render a full-page bail-out below.
  const hasFullPageError = !!(error != null && error.isNetworkError) || (error == null ? void 0 : error.status) === 500 || (error == null ? void 0 : error.status) === 429;
  // Redirecting (or about to) — show the loading layout, not an error flash.
  const holdForRedirect = !!redirectUrl || domainActionLoading && hasFullPageError;
  const showSkeleton = isLoading || holdForRedirect;
  const reportSearchBi = !!domain && !isLoading && !domainActionLoading && !redirectUrl;
  useEffect(() => {
    if (!reportSearchBi || !domainActionStatus || domainActionStatus === DomainSearchError.NO_STATUS) {
      return;
    }
    DomainSearchSuiteModule.internals.bi.onSearchResultLoad == null || DomainSearchSuiteModule.internals.bi.onSearchResultLoad({
      result: domainActionStatus,
      domain,
      type: 'search-result'
    });
  }, [reportSearchBi, domainActionStatus, domain]);
  useEffect(() => {
    if (!redirectUrl || !domain) {
      return;
    }
    if (onDomainNotFound) {
      onDomainNotFound(domain);
    } else {
      // Fallback when no handler is provided (Storybook, tests).
      history.replace(redirectUrl);
    }
  }, [redirectUrl, domain, history, onDomainNotFound]);
  if (!domain) {
    return /*#__PURE__*/React.createElement(Box, {
      align: "center",
      padding: "SP10"
    }, /*#__PURE__*/React.createElement(Text, null, t('domain_search_gate.whois_results.no_domain')));
  }

  // Full-page errors bail out before the layout renders (skipped while redirecting).
  if (!holdForRedirect && error != null && error.isNetworkError) {
    return /*#__PURE__*/React.createElement(WhoisPageError, {
      message: "domain_search_gate.whois_results.error.full_page.connection.description",
      onRetry: retry
    });
  }
  if (!holdForRedirect && ((error == null ? void 0 : error.status) === 500 || (error == null ? void 0 : error.status) === 429)) {
    return /*#__PURE__*/React.createElement(WhoisPageError, {
      message: "domain_search_gate.whois_results.error.full_page.server.description",
      onRetry: retry
    });
  }
  return /*#__PURE__*/React.createElement(Box, {
    display: "block",
    boxSizing: "border-box",
    width: "100%"
  }, /*#__PURE__*/React.createElement(Box, {
    className: mobileOptimizations ? `${s.whoisResultsPage} ${s.mobileOptimized}` : s.whoisResultsPage,
    backgroundColor: "white"
  }, /*#__PURE__*/React.createElement(Box, {
    gridArea: "header",
    direction: "vertical",
    gap: "SP4",
    padding: mobileOptimizations ? '0 0 SP4 0' : 'SP4 0'
  }, /*#__PURE__*/React.createElement(HeaderWrapper, {
    onBackClick: mobileOptimizations ? undefined : handleBackClick
  }, /*#__PURE__*/React.createElement(Heading, {
    size: "large",
    as: "h2"
  }, t('domain_search_gate.whois_results.title'))), /*#__PURE__*/React.createElement(DomainSearchSuiteModule.SDK.Components.DomainSearchBar, {
    compact: true,
    showOptionsIfEmptyInput: false,
    autoFocus: false,
    useModalToolbar: mobileOptimizations,
    placeholder: mobileOptimizations ? t('domains-search-suite-translations-ns:DomainSearch.Mobile.searchInput.placeholder') : undefined,
    onSubmit: handleSearchSubmit
  }), !showSkeleton && !mobileOptimizations && /*#__PURE__*/React.createElement(Box, {
    align: "center"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "small",
    className: s.statusText
  }, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "domain_search_gate.whois_results.status.domain_taken",
    values: {
      domainName: domain
    },
    components: [/*#__PURE__*/React.createElement(Text, {
      size: "small",
      weight: "bold"
    })]
  })))), /*#__PURE__*/React.createElement(Box, {
    className: s.whoisContent,
    direction: "vertical",
    gap: "SP6",
    padding: mobileOptimizations ? 'SP1 0 SP5 0' : 'SP4 0'
  }, showSkeleton && /*#__PURE__*/React.createElement(WhoisSkeleton, {
    domain: domain
  }), !showSkeleton && error && !data && /*#__PURE__*/React.createElement(WhoisInlineError, {
    subtitle: error.message,
    onRetry: retry
  }), !showSkeleton && data && (() => {
    const rawOnly = data.source !== 'RDAP' && !data.domainInfo.expires && !!(data.raw.registry || data.raw.registrar);
    const showAll = !mobileOptimizations || infoExpanded;
    return /*#__PURE__*/React.createElement(Box, {
      ref: whoisCardRef,
      className: mobileOptimizations ? undefined : s.whoisCard,
      direction: "vertical",
      borderWidth: "1px",
      borderStyle: "solid",
      borderColor: "D60",
      borderRadius: "12px",
      overflow: "hidden"
    }, /*#__PURE__*/React.createElement(Card, null, !mobileOptimizations && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Card.Header, {
      title: /*#__PURE__*/React.createElement(Heading, {
        size: "large",
        as: "h2"
      }, domain),
      subtitle: t('domain_search_gate.whois_results.info_card.subheader')
    }), /*#__PURE__*/React.createElement(Divider, null)), /*#__PURE__*/React.createElement(Card.Content, null, rawOnly ? /*#__PURE__*/React.createElement(RawOnlySection, {
      raw: data.raw
    }) : /*#__PURE__*/React.createElement(Box, {
      direction: "vertical",
      gap: "SP6"
    }, /*#__PURE__*/React.createElement(DomainInfoSection, {
      domainInfo: data.domainInfo,
      collapsed: mobileOptimizations && !infoExpanded
    }), showAll && /*#__PURE__*/React.createElement(React.Fragment, null, (data.registrar.name || data.registrar.url || data.registrar.whoisServer) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Divider, null), /*#__PURE__*/React.createElement(RegistrarSection, {
      registrar: data.registrar,
      whoisBiParams: whoisBiParams
    })), /*#__PURE__*/React.createElement(Divider, null), /*#__PURE__*/React.createElement(RegistrantSection, {
      registrant: data.registrant
    }), (data.raw.registry || data.raw.registrar) && /*#__PURE__*/React.createElement(React.Fragment, null, !mobileOptimizations && /*#__PURE__*/React.createElement(Divider, null), /*#__PURE__*/React.createElement(RawDataSection, {
      raw: data.raw,
      whoisBiParams: whoisBiParams
    })), (data.tos.text || data.tos.url) && /*#__PURE__*/React.createElement(TosSection, {
      tos: data.tos,
      whoisBiParams: whoisBiParams
    })), mobileOptimizations && /*#__PURE__*/React.createElement(Box, {
      align: "center"
    }, /*#__PURE__*/React.createElement(TextButton, {
      dataHook: "whois-info-toggle",
      underline: "always",
      skin: "dark",
      onClick: handleToggleInfo,
      suffixIcon: infoExpanded ? /*#__PURE__*/React.createElement(ChevronUp, null) : /*#__PURE__*/React.createElement(ChevronDown, null)
    }, t(infoExpanded ? 'domain_search_gate.whois_results.mobile.collapse.cta' : 'domain_search_gate.whois_results.mobile.expand.cta')))))));
  })()), /*#__PURE__*/React.createElement(Box, {
    ref: alternativesPanelRef,
    className: s.alternativesPanel,
    direction: "vertical",
    gap: "SP4"
  }, /*#__PURE__*/React.createElement(AlternativesPanel, {
    voucherBannerRef: voucherBannerRef,
    reportOfferingsBi: reportSearchBi
  }))), /*#__PURE__*/React.createElement(ExploreSimilarButton, {
    visible: showExploreButton,
    onClick: handleExploreClick
  }));
};
export const WhoisResultsPage = _ref3 => {
  let {
    handlers,
    onDomainNotFound
  } = _ref3;
  useSettledMethods(ANONYMOUS_SETTLEMENTS);
  return /*#__PURE__*/React.createElement(DomainSearchGateModule.SDK.Components.ModalsProvider, null, /*#__PURE__*/React.createElement(DomainsApiClient.Provider, null, /*#__PURE__*/React.createElement(DomainSearchSuiteModule.Provider, {
    translations: {
      Loading: /*#__PURE__*/React.createElement(React.Fragment, null)
    },
    actions: {
      handlers
    }
  }, /*#__PURE__*/React.createElement(WhoisResultsPageContent, {
    onDomainNotFound: onDomainNotFound
  }))));
};
//# sourceMappingURL=WhoisResultsPage.js.map